import { Button, message } from "antd";
import { useEffect, useState } from "react";
import StepCard from "../ui/StepCard";
import { Card, ListItem } from "./SectionTwo.default";
import CompletedStageCard from "../ui/CompletedStageCard";

const awarnessStageStepCards: Card[] = [
  {
    count: 1,
    status: "completed",
    title: "Allocate Budget and Resources",
    listTitle: "Instructions:",
    checkList: (
      <>
        <li className="font-bold">
          Set Budgets for Each Program Category: Use the provided fields to
          allocate resources for each program category (e.g., Mental Health,
          Fitness, Educational). Ensure the allocation reflects organizational
          priorities and well-being goals.
        </li>
        <li>
          Monitor the effectiveness of your budget and resource allocation
          through the Analytics - Budget and Resource Utilization page.
          Adjustments can only be made by starting a new allocation session.
        </li>
        <li>
          Click Save Budget to finalize your allocations. Once saved, the
          allocations will be locked, and the platform will notify the assigned
          teams that you assigned in about their responsibilities via email and
          platform notification.
        </li>
        <li>
          Once the policy is sent, you may not edit it. If you need to make
          updates, please create a new communication by clicking "START".
        </li>
      </>
    ),
    backgroundColor: "#d17f42",
    actions: [
      {
        name: "start",
        action: () => {
          message.info("This action is not yet available.");
        },
      },
    ],
  },
  {
    count: 2,
    status: "completed",
    title: "Develop Key Metrics",
    listTitle: "Instructions:",
    checkList: (
      <>
        <li>
          Identify the specific objectives you want to achieve for workplace
          well-being, such as improving employee engagement, reducing
          absenteeism, or enhancing participation in programs.
        </li>
        <li>
          Set Baselines and Targets: Establish baseline data for each metric
          (e.g., current absenteeism rates, engagement scores) and set realistic
          improvement targets. Example: Increase engagement scores by 15% by
          year-end.
        </li>

        <li>
          Save and Finalize Metrics: Click Save Metrics to lock your selections.
          Once saved, metrics will be displayed in the platform’s Key Metrics
          Dashboard, allowing you to track progress over time.
        </li>
        <li>
          Once metrics are saved, they cannot be edited. To update or revise
          metrics, you must start a new session by clicking "START NEW
        </li>
      </>
    ),
    backgroundColor: "#8eb1bd",
    actions: [
      {
        name: "start",
        action: () => {
          message.info("This action is not yet available.");
        },
      },
    ],
  },
  {
    count: 3,
    status: "completed",
    title: "Create and Allocate Programs",
    listTitle: "Instructions:",
    checkList: (
      <>
        <li>
          Initiate Program Creation: Click "Create Program" to start a new
          initiative tailored to employee needs.
        </li>
        <li>
          Choose a Program Category: Select the appropriate category from the
          dropdown menu to align the program with organizational goals.
        </li>
        <li>
          You may crete your own program or choose one of NUVO WELL existing
          programs.
        </li>
        <li>
          Use the Analytics - Usage Analytics -Program Enrolment page to monitor
          employee engagement and program performance.
        </li>
        <li>
          Once the program is published, it cannot be edited. To make changes,
          you must create a new program by clicking "START NEW PROGRAM"
        </li>
      </>
    ),
    backgroundColor: "#bdb0ac",
    actions: [
      {
        name: "Create",
        action: () => {
          message.info("This action is not yet available.");
        },
      },
    ],
  },
  {
    count: 4,
    status: "completed",
    title: "Create and Send Monthly Surveys",
    listTitle: "Instructions:",
    checkList: (
      <>
        <li>
          Initiate Survey Creation: Click "Create Survey" to design a new survey
          tailored to your well-being goals.
        </li>
        <li>
          Design Your Survey: Title: Provide a descriptive name for the survey
          (e.g., "Monthly Well-Being Feedback"). Description: Briefly outline
          the survey’s purpose (e.g., "Gather feedback on workload, management
          support, and engagement levels").
        </li>
        <li>Assign the survey to specific ompanies or department.</li>
        <li>
          Monitor Survey Progress: Track response rates and completion status in
          the Analytics - Survey and Feedback Metrics.
        </li>
        <li>
          Surveys can only be edited before they are sent. After publishing,
          changes cannot be made. To update questions or assignments, create a
          new survey by clicking "START NEW SURVEY
        </li>
      </>
    ),
    backgroundColor: "#7c9281",
    actions: [
      {
        name: "Create",
        action: () => {
          message.info("This action is not yet available.");
        },
      },
    ],
  },
];

const completedStageList: ListItem[] = [
  {
    name: "Review Your Inputs:",
    items: [
      "Visit the summary page to review all your assigned budgets, defined metrics, and created programs.",
      "Click Edit to make any necessary changes to your allocations, metrics, or programs.",
    ],
  },
  {
    name: "Submit the Planning & Actions Stage:",
    items: [
      "Once you’re satisfied with your inputs, click the Submit  button.",
      "A confirmation popup will appear. Confirm to lock your inputs and generate a detailed final report.",
    ],
  },
  {
    name: "Post-Submission Access:",
    items: [
      "After submission, this stage will be locked for edits but will remain accessible for viewing and exporting reports.",
      "If you need to make changes after submission, contact your platform administrator to reopen the stage.",
    ],
  },
  {
    name: "Move to the Next Stage:",
    items: [
      "Upon submission, you’ll be automatically redirected to the Overview page, where you can continue with the Evaluation & Monitoring Stage.",
      "Refer back to this stage anytime by clicking on the Planning & Actions Stage tab in the Overview.",
    ],
  },
];

const completedStageTitle = "Completing the Planning & Actions Stage";

const subTitleText = (
  <p>
    Congratulations on completing the Planning & Actions stage! Follow these
    steps to finalize this stage:
  </p>
);

function SectionTwo() {
  const [submitActive, setSubmitActive] = useState(false);
  const [showCompletedStage, setShowCompletedStage] = useState(false);

  //Lifecycle
  useEffect(() => {
    findUncompletedSteps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Events
  function onSubmit() {
    setShowCompletedStage(true);
  }

  // Methods
  function findUncompletedSteps() {
    const uncompletedSteps = awarnessStageStepCards.filter(
      (step) => step.status !== "completed",
    );

    if (uncompletedSteps.length) {
      setSubmitActive(false);
    } else {
      setSubmitActive(true);
    }

    return uncompletedSteps;
  }

  return (
    <section className="mx-auto max-w-[1920px] px-16 py-16">
      {/* Step Cards */}
      {awarnessStageStepCards.map((card) => {
        return (
          <StepCard
            key={card.count}
            countValue={card.count}
            status={card.status}
            title={card.title}
            checkListTitle={card.listTitle}
            checkList={card.checkList}
            backgroundColor={card.backgroundColor}
            actions={card.actions}
          />
        );
      })}
      {/* Submit Button */}
      <div className="flex justify-center py-8">
        <Button
          disabled={!submitActive}
          className={`bg-[#ffc107] px-16 py-8 text-lg font-bold tracking-wide ${submitActive && "hover:text-white"} `}
          onClick={onSubmit}
        >
          SUBMIT
        </Button>
      </div>
      {/* Completed Stage Component That Shows On Submmit */}
      {(showCompletedStage || submitActive) && (
        <CompletedStageCard
          title={completedStageTitle}
          subTitleText={subTitleText}
          list={completedStageList}
        />
      )}
    </section>
  );
}

export default SectionTwo;
